import React from "react";
import styled from "styled-components";
import useTheme from "../../lib/useTheme";
import { mergeStrings } from "../../lib/helpers";
import copyIcon from "./copyIcon.png";
import Button from "../Button";
import TabList from "../TabList";
import Tab from "../Tab";

export interface DGA_CodeSnippetProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  type: "singleLine" | "multiLine";
  multiLineItems?: {
    title: React.ReactNode;
    code: string;
    active?: boolean;
  }[];
  onTabClicked?: (index: number, element: any) => void;
}

const CodeSnippet: React.FC<DGA_CodeSnippetProps> = ({
  children,
  type = "singleLine",
  multiLineItems,
  onTabClicked,
  ...props
}) => {
  const theme = useTheme();
  const [showCopiedText, setShowCopiedText] = React.useState(false);

  const copyHandler = () => {
    setShowCopiedText(true);
    setTimeout(() => {
      setShowCopiedText(false);
    }, 800);

    if (navigator.clipboard) {
      const text = document.getElementById("dgaui_codeSnippetText")?.innerText;
      navigator.clipboard.writeText(text!);
    }
  };

  const activeCode = React.useMemo(() => {
    let result = [""];
    if (multiLineItems) {
      const newActiveCode = multiLineItems.find((el) => el.active)?.code || "";
      result = newActiveCode.split("\n");
    }

    return result;
  }, [multiLineItems]);

  const copyButton = (
    <Button
      iconOnly
      leadIcon={showCopiedText ? "copied!" : <img src={copyIcon} width={20} />}
      size="medium"
      style="subtle"
      buttonStyle={{ padding: 4 }}
      onClick={copyHandler}
    />
  );

  return (
    <StyledComponent
      {...props}
      className={mergeStrings("dgaui dgaui_codeSnippet " + type, props.className)}
      $theme={theme}
    >
      {type === "singleLine" && (
        <div className="singleLine">
          <div className="content" id="dgaui_codeSnippetText">
            {children}
          </div>
          <div className="copyIcon">{copyButton}</div>
        </div>
      )}

      {type === "multiLine" && multiLineItems && (
        <div className="multiLine">
          <TabList>
            {multiLineItems.map((el, i) => (
              <Tab selected={el.active} onClick={() => onTabClicked?.(i, el)}>
                {el.title}
              </Tab>
            ))}
          </TabList>
          <div className="content">
            <pre className="linesNumbers">
              <code>
                {Array(activeCode.length)
                  .fill("")
                  .map((el, i) => i + 1 + "\n")}
              </code>
            </pre>
            <pre>
              <code className="code" id="dgaui_codeSnippetText">
                {activeCode.map((el) => el + "\n")}
              </code>
            </pre>
          </div>
          <div className="floatCopyIcon">{copyButton}</div>
        </div>
      )}
    </StyledComponent>
  );
};

const StyledComponent = styled.div<{
  $theme: Theme;
}>`
  direction: ltr;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${(p) => p.$theme.palette.neutral[200]};

  .singleLine {
    padding: 24px;
    border-radius: 16px;
    background-color: #fff;
    border: 1px solid ${(p) => p.$theme.palette.neutral[200]};
    display: flex;
    justify-content: space-between;
    align-items: center;
    .content {
      font-size: 16px;
      color: ${(p) => p.$theme.palette.primary[600]};
    }
  }

  .multiLine {
    position: relative;
    .floatCopyIcon {
      position: absolute;
      z-index: 1;
      inset-inline-end: 24px;
      top: 74px;
    }

    .content {
      display: flex;
      font-size: 16px;

      .linesNumbers {
        background-color: ${(p) => p.$theme.palette.neutral[50]};
        padding: 24px;
        border-right: 1px solid ${(p) => p.$theme.palette.neutral[200]};
        font-weight: 500;
        box-sizing: border-box;
        color: #667085;
      }

      .code {
      }

      pre {
        margin: 0;
        padding: 24px;
        overflow: scroll;
      }
    }
  }
`;

export default CodeSnippet;
